import React from 'react'
import { View, Text, TouchableOpacity, LayoutChangeEvent, ViewStyle, StyleSheet, Dimensions } from 'react-native'
import _ from 'lodash'

import CONS from 'x/config/constants'
import p from 'x/config/platform-specific'
import { S, COLORS } from 'x/config/styles'
import {
  IBarcodeScannerOutput,
  IXSegmentOption,
  IBarcodeScannerWithSegmentProps,
  ICustomBarcodeRenderOption,
  ICustomBarcodeRenderProps,
} from 'x/index'
import * as util from 'x/utils/util'

import XInput from './XInput'
import XSegment from './XSegment'
import BarcodeScanner from './BarcodeScanner'
import XText from './XText'
import VStack from './VStack'

const MAX_SCREEN_WIDTH = p.op.isWeb() ? Math.floor(CONS.SPECIFIC_PLATFORM.WEB.MAIN_CONTENT_WIDTH / 3) : Dimensions.get('screen').width
const DEFAULT_CAMERA_WIDTH = Math.floor(0.9 * MAX_SCREEN_WIDTH)

function getDefaultSegmentOptions(isForceTypingText?: boolean): ICustomBarcodeRenderOption[] {
  if (isForceTypingText) {
    return [
      {
        key: 'text',
        label: 'พิมพ์',
        customRender: (cProps: ICustomBarcodeRenderProps) => <BarcodeTextInput {...cProps} />,
      },
    ]
  }

  if (p.op.isWeb()) {
    return [
      {
        key: 'camera',
        label: 'สแกน/พิมพ์',
        customRender: (cProps: ICustomBarcodeRenderProps) => <DefaultBarcodeScanner {...cProps} />,
      },
    ]
  }

  return [
    {
      key: 'camera',
      label: 'สแกน',
      customRender: (cProps: ICustomBarcodeRenderProps) => <DefaultBarcodeScanner {...cProps} />,
    },
    {
      key: 'text',
      label: 'พิมพ์',
      customRender: (cProps: ICustomBarcodeRenderProps) => <BarcodeTextInput {...cProps} />,
    },
  ]
}

const BarcodeScannerWithSegment: React.FC<IBarcodeScannerWithSegmentProps> = ({
  initialIndex = 0,
  disabled = false,
  cameraContainerHeight = 200,
  cameraContainerWidth = DEFAULT_CAMERA_WIDTH,
  segmentContainerStyle = {},
  segmentTextStyle = {},
  hideSegment = false,
  forceTypingText = false,
  ...props
}) => {
  const [options, setOptions] = React.useState<ICustomBarcodeRenderOption[]>(getDefaultSegmentOptions(forceTypingText))
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false)
  const [cameraContainerStyle, setCameraContainerStyle] = React.useState<ViewStyle>({ minHeight: 120, height: 200 })

  const [selectedSegmentIndex, setSelectedSegmentIndex] = React.useState<number>(
    p.op.isIOS() && initialIndex === 0 && !props.selectedIndex ? 1 : 0
  )
  const [isBarcodeRead, setIsBarcodeRead] = React.useState<boolean>(false)

  const [isSegmentTransitioning, setIsSegmentTransitioning] = React.useState<boolean>(false)

  React.useEffect(() => {
    if (_.isBoolean(forceTypingText) && _.isNil(props.overrideSegmentOptions)) {
      const newOptions = getDefaultSegmentOptions(forceTypingText)
      setOptions(newOptions)
      if (selectedSegmentIndex > newOptions.length - 1) {
        setSelectedSegmentIndex(newOptions.length - 1)
      }
    }
  }, [forceTypingText])

  React.useEffect(() => {
    if (_.isArray(props.extraSegmentOptions) && _.isNil(props.overrideSegmentOptions)) {
      const newOptions = [...getDefaultSegmentOptions(forceTypingText)]
      for (const newOpt of props.extraSegmentOptions) {
        const duplicatedKeyIdx = options.findIndex((opt) => opt.key === newOpt.key)
        if (duplicatedKeyIdx > -1) {
          newOptions[duplicatedKeyIdx] = newOpt
        } else {
          newOptions.push(newOpt)
        }
      }
      setOptions(newOptions)
    }
  }, [props.extraSegmentOptions])

  React.useEffect(() => {
    if (_.isArray(props.overrideSegmentOptions)) {
      const newOptions = [...props.overrideSegmentOptions]
      for (const newOpt of props.overrideSegmentOptions) {
        const defaultKeyIdx = getDefaultSegmentOptions(forceTypingText).findIndex((opt) => opt.key === newOpt.key)
        if (defaultKeyIdx > -1) {
          const defaultKey = newOptions[defaultKeyIdx].key
          const defaultOption = getDefaultSegmentOptions(forceTypingText).find((opt) => opt.key === defaultKey)
          newOptions[defaultKeyIdx] = defaultOption
        } else {
          newOptions.push(newOpt)
        }
      }
      setOptions(newOptions)
    }
  }, [props.overrideSegmentOptions])

  async function _onBarcodeScanned(barcodeOutput: IBarcodeScannerOutput) {
    setIsBarcodeRead(true)
    await util.delay(100)
    await props.onBarcodeScanned(barcodeOutput)
    await util.delay(200)
    setIsBarcodeRead(false)
  }

  function _getSegmentOptions(): IXSegmentOption[] {
    return options.map((opt) => ({
      label: opt.label,
      value: opt.key,
    }))
  }

  async function _onSegmentChange(opt, idx) {
    if (isSegmentTransitioning) {
      return
    }
    setIsSegmentTransitioning(true)
    if (_.isFunction(props.onChangeSegment)) {
      await props.onChangeSegment(idx)
      setIsSegmentTransitioning(false)
      return
    }
    setSelectedSegmentIndex(idx)
    await util.delay(500)
    setIsSegmentTransitioning(false)
  }

  const _renderCustomBarcodeScanner = () => {
    const opt = options[selectedSegmentIndex]
    if (_.isNil(opt)) {
      return <XText>Error: Invalid segment option selected.</XText>
    }

    const extraContainerStyle: ViewStyle = {}
    if (cameraContainerWidth) {
      extraContainerStyle.width = cameraContainerWidth
    }
    if (cameraContainerHeight) {
      extraContainerStyle.height = cameraContainerHeight
    }

    const containerStyle: ViewStyle = StyleSheet.flatten([cameraContainerStyle, extraContainerStyle])
    const isDisabled = isBarcodeRead || disabled

    if (opt.customRender) {
      return (
        <VStack w='full' alignItems='center' pt='2' style={containerStyle}>
          {opt.customRender({
            ...props,
            disabled: isDisabled,
            barcodeReadHandler: _onBarcodeScanned,
            containerStyle,
          })}
        </VStack>
      )
    }

    return <XText>Error: No custom render function provided for this segment.</XText>
  }

  async function _onLayout(evt: LayoutChangeEvent) {
    const { width } = evt.nativeEvent.layout
    setCameraContainerStyle({ ...cameraContainerStyle, width })
    await util.delay(200)
    setIsInitialized(true)
  }

  return (
    <View style={[S.COLUMN_CENTER_TOP]} onLayout={_onLayout}>
      <SegmentPropsManager
        isInitialized={isInitialized}
        initialIndex={initialIndex}
        selectedIndex={props.selectedIndex}
        stateSegmentIndex={selectedSegmentIndex}
        setSelectedSegmentIndex={setSelectedSegmentIndex}
      />
      {isInitialized && options.length > 1 && !hideSegment ? (
        <View style={[{ width: cameraContainerStyle.width }, cameraContainerWidth ? { width: cameraContainerWidth } : {}]}>
          <XSegment
            segmentTextStyle={[S.TEXT_LARGER, segmentTextStyle]}
            options={_getSegmentOptions()}
            selectedIndex={selectedSegmentIndex}
            onSegmentChange={_onSegmentChange}
            segmentContainerStyle={segmentContainerStyle}
          />
        </View>
      ) : null}
      {isInitialized ? _renderCustomBarcodeScanner() : <XText>Initializing...</XText>}
    </View>
  )
}

// create this seperate to avoid useEffect memory leaked
const SegmentPropsManager = ({ isInitialized, initialIndex, selectedIndex, stateSegmentIndex, setSelectedSegmentIndex }) => {
  React.useEffect(() => {
    let isActive = true
    if (isInitialized && _.isNumber(initialIndex) && !selectedIndex) {
      if (p.op.isIOS()) {
        setTimeout(() => {
          if (isActive) {
            setSelectedSegmentIndex(initialIndex)
          }
        }, 300)
      } else {
        setSelectedSegmentIndex(initialIndex)
      }
    } else if (isInitialized && selectedIndex) {
      setSelectedSegmentIndex(selectedIndex)
    }
    return () => {
      isActive = false
    }
  }, [isInitialized])

  React.useEffect(() => {
    if (isInitialized && stateSegmentIndex !== selectedIndex) {
      setSelectedSegmentIndex(selectedIndex)
    }
  }, [selectedIndex])

  return null
}

const DefaultBarcodeScanner = (props: ICustomBarcodeRenderProps) => (
  <BarcodeScanner {...props} onBarcodeScanned={props.barcodeReadHandler} />
)

const BarcodeTextInput = (props: ICustomBarcodeRenderProps) => {
  const [barcodeText, setBarcodeText] = React.useState('')
  const [isBarcodeRead, setIsBarcodeRead] = React.useState(false)

  const isBarcodeScannerDisabled = isBarcodeRead || props.disabled

  function _onChangeText(newText) {
    setBarcodeText(newText)
  }

  async function _onSubmitBarcode() {
    setIsBarcodeRead(true)
    await util.delay(100)
    await props.barcodeReadHandler({ data: barcodeText, dataRaw: barcodeText })
    await util.delay(500)
    setBarcodeText('')
    setIsBarcodeRead(false)
  }

  const maxWidth = props.containerStyle.width as number

  return (
    <View style={[S.COLUMN_CENTER_TOP, S.BG_LIGHT_GREY, props.containerStyle]}>
      {props.inputLabel && props.inputLabel !== '' ? (
        <View style={[S.COLUMN_CENTER_TOP, S.WIDTH_FULL, S.MARGIN_TOP_6]}>
          <Text style={[S.TEXT_INACTIVE, S.TEXT_LARGER]}>{props.inputLabel}</Text>
        </View>
      ) : null}
      <View style={[S.COLUMN_CENTER_TOP, S.HEIGHT_34, S.MARGIN_TOP_6]}>
        <XInput
          isDisabled={isBarcodeScannerDisabled}
          placeholder={props.inputPlaceholder && props.inputPlaceholder !== '' ? props.inputPlaceholder : 'พิมพ์เลขบาร์โค้ด...'}
          autoFocus
          textAlign='center'
          style={{ width: Math.floor(maxWidth * 0.8) }}
          value={barcodeText}
          onChangeText={_onChangeText}
          onSubmitEditing={_onSubmitBarcode}
        />
      </View>
      <View style={[S.ROW_CENTER, S.MARGIN_TOP_6, S.MARGIN_BOTTOM_12]}>
        <TouchableOpacity
          onPress={_onSubmitBarcode}
          disabled={isBarcodeScannerDisabled}
          style={{
            backgroundColor: isBarcodeRead ? COLORS.TEXT_INACTIVE : COLORS.APP_MAIN,
            borderRadius: 7,
            height: 28,
            width: 80,
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <XText bold style={[isBarcodeRead ? S.TEXT_ACTIVE : S.TEXT_ACTIVE_DARK, S.TEXT_LARGER]}>
            ยืนยัน
          </XText>
        </TouchableOpacity>
      </View>
    </View>
  )
}

export default BarcodeScannerWithSegment
